/**
 * The gate: nobody signed in is the sign-in screen; somebody is the shell —
 * once the offline store is theirs. `bindAccount` runs before any cached row
 * is read (25B rule 7), so a second person on this browser never sees the
 * first one's rows, not even for a frame.
 */
import { useEffect, useState, type ReactNode } from "react";

import { bindAccount } from "../offline/cache";
import { openOffline } from "../offline/idb";
import { usePrefs } from "../prefs/store";
import { Shell } from "../shell/Shell";
import { MeContext } from "./me";
import { useSession, type Me } from "./session";
import { SignIn } from "./SignIn";

export function App() {
  const session = useSession();
  if (session.isPending) return null;
  if (!session.data) return <SignIn />;
  return (
    <Bound me={session.data}>
      <Shell />
    </Bound>
  );
}

function Bound({ me, children }: { me: Me; children: ReactNode }) {
  const owner = me.account_id;
  const [bound, setBound] = useState<number | null>(null);
  useEffect(() => {
    let live = true;
    void (async () => {
      const db = await openOffline();
      try {
        await bindAccount(db, owner);
      } finally {
        db.close();
      }
      usePrefs.getState().bind(me.account_id);
      if (live) setBound(owner);
    })();
    return () => {
      live = false;
    };
  }, [owner, me.account_id]);
  if (bound !== owner) return null;
  return <MeContext.Provider value={me}>{children}</MeContext.Provider>;
}
